// review-item-bodies — the rich per-kind review renderers.
//
// Extracted from the former ReviewQueueView page so they can be hosted both
// inline and inside the Kanban's <ReviewItemPanel> drawer. Each body owns the
// approve / reject / request-revision dispatch for its item kind:
//
//   - CallDraftsBody      → /api/agent/reviewables/call-* (+ "approve all")
//   - MonthlyNarrativeBody → /api/agent/reviewables/monthly-narrative-draft
//   - ComposerDraftBody    → the per-agent composition workbench (owns its own
//                            approve / reject / request-revision)
//   - TaskBody             → AgentContext.reviewTask (PATCH /api/agent/tasks/:id/approve|reject)
//
// Adding a new artefact type? Extend `useReviewQueue` with the item shape and
// add a renderer here.

import type {
    AgentTask,
    CoacNoticeDate,
    ContextTranscriptLine,
    KnowledgeAssignmentTarget,
    RelationshipPromotion,
    RelationshipProposalRow,
    RelationshipRoleDto,
    ReviewDecision,
} from '@septima/contract';
import type { ReactNode } from 'react';
import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
    AlertTriangle,
    CheckCircle2,
    FileText,
    Quote,
    RotateCcw,
    ThumbsDown,
    ThumbsUp,
    XCircle,
} from 'lucide-react';
import { RELATIONSHIP_ENDPOINT_TYPE_LABELS } from '@septima/contract';
import { Pill, Button, Select } from '@/components/ui';
import {
    coacElectionOptionsProblem,
    type CoacElectionOption,
} from '@/components/coac/CoacDecisionField';
import EmptyState from '@/components/EmptyState';
import { useAgent } from '@/contexts/AgentContext';
import { useSession } from '@/contexts/SessionContext';
import { canOpenRoute } from '@/lib/frontends';
import { useQueryClient } from '@tanstack/react-query';
// `agent-rows/hooks` owns the `['agent', 'rows', …]` key and the shape stored
// under it. Each body below reads one projection of that shape; none of them
// declares the key a second time.
import {
    useCallActionItems,
    useCallFacts,
    useCallKbNotes,
    useCoacActionDrafts,
    useComposerDrafts,
    useMonthlyNarrativeDrafts,
    type AgentRowReviewStatus,
    type CallActionItemRow,
    type CallFactRow,
    type CallKbNoteRow,
} from '@/lib/agent-rows/hooks';
import {
    approveReviewable,
    rejectReviewable,
    requestRevisionReviewable,
    submitReviewDecisions,
} from '@/lib/agent-reviewables';
import { blockReason, buildDecision, readingIsCurrent } from '@/lib/review-decision';
import { useToast } from '@/lib/useToast';
import {
    assignableRecordsFrom,
    assignmentForDecision,
    isTargetSelected,
    preTickedTargets,
    toggleTarget,
    type AssignableRecord,
} from '@/lib/knowledge-assignment';
import { useRelationshipRoles, useRelationships } from '@/lib/relationships/hooks';
import { CounterpartPicker } from '@/components/relationships/RelationshipsPanel';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/coac/format';
import {
    summariseArtefact,
    type CallDraftsReviewItem,
    type CoacActionDraftReviewItem,
    type ComposerDraftReviewItem,
    type MonthlyNarrativeReviewItem,
    type TaskReviewItem,
} from '@/lib/use-review-queue';
import CitedSummary from '../CitedSummary';
import {
    ContextBlocks,
    ContextSection,
    CitedTranscriptExpander,
    transcriptFrom,
    useReviewContext,
} from './ContextSection';
import { DownloadRow } from '../AgentTaskCard';
import { ExportToSharePointButton } from '../ExportToSharePointButton';
import RejectionDialog from '../RejectionDialog';
import CompositionWorkbench from '../composition-workbench';
import { workbenchKindForAgent } from '../composition-workbench/composition-agent-config';
import { invariantsFor, sectionsFor } from '../composition-workbench/kinds/draft-sections';

// -------- Call drafts body --------
//
// Staging review: each fact / action / note is a tri-state — accept, reject, or
// leave undecided (the default). The thumbs only STAGE a decision; nothing is
// committed until "Confirm". Undecided rows are simply omitted from the batch,
// so they stay pending in the queue. One batch request commits the lot.

type StageDecision = 'approve' | 'reject';

/**
 * The four kinds this panel stages. It is exactly the set of kinds whose spec
 * groups `{ by: 'callId' }`, and it is narrower than `ReviewableKind` on
 * purpose: `buildDecision` then knows which of the two payloads each staged
 * row can carry, so a promotion cannot be staged on a fact.
 */
type CallDraftKind =
    | 'call-extracted-fact'
    | 'call-action-item'
    | 'call-kb-note'
    | 'relationship-proposal';

interface Staged {
    kind: CallDraftKind;
    decision: StageDecision;
    reason?: string;
    /** The records this accepted insight applies to (SEP-183 / ADR-0015).
     *  Empty means the insight stays at Contact level and assigns nothing. */
    assignTo?: KnowledgeAssignmentTarget[];
    /** True once the reviewer answered the picker themself. Their answer then
     *  survives a thumb-down and a return to the thumb-up. */
    assignTouched?: boolean;
    /** The counterparty and the two roles the reviewer bound on a relationship
     *  proposal (SEP-215). Absent means "approve the claim, write no link",
     *  which is the honest answer when the reviewer cannot yet say who the
     *  other person is. Meaningful on `relationship-proposal` alone. */
    promoteTo?: RelationshipPromotion;
}

/**
 * The promotion the election control opens on.
 *
 * The server ranks the reader's own Contacts against the name in the transcript
 * and returns the list in order. The top candidate pre-fills the picker so the
 * common case is one press — and its REASON renders beside it, because the
 * ranking suggests and never binds: a telephone number is evidence, a matching
 * surname is not. With no candidate at all there is nothing to open on, and the
 * reviewer picks or creates the contact themself.
 *
 * Both ends seed from the proposed role. That is right for the symmetric pairs
 * the graph is mostly made of (sibling, spouse, business partner) and wrong for
 * the rest, so both selects are on screen from the start.
 */
function initialPromotion(proposal: RelationshipProposalRow): RelationshipPromotion | undefined {
    const suggested = proposal.counterpartySuggestions[0];
    if (!suggested) return undefined;
    return {
        counterparty: { kind: 'existing-contact', contactId: suggested.contactId },
        subjectRole: proposal.proposedRole,
        counterpartyRole: proposal.proposedRole,
    };
}

/** Split a transcript name into the two boxes a draft Contact needs. */
function splitPersonName(personName: string): { firstName: string; lastName: string } {
    const parts = personName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '', lastName: '' };
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

export function CallDraftsBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: CallDraftsReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    const allFacts = useCallFacts();
    const allActions = useCallActionItems();
    const allNotes = useCallKbNotes();
    const { pushToast } = useToast();
    const queryClient = useQueryClient();

    // Live call context (header + transcript) — which call, when, who. The
    // transcript drives the per-fact "show in transcript" expander.
    const contextBlocks = useReviewContext('call', item.callId);
    const headerBlocks = contextBlocks.filter((b) => b.type !== 'transcriptExcerpt');
    const transcript = transcriptFrom(contextBlocks);

    // The records this call's PERSON carries (SEP-183 / ADR-0015). The
    // contact-to-record join already exists, so the picker reuses the shared
    // `useRelationships` hook rather than adding a second query. One key, one
    // shape: a second query on the same key with a different body is the
    // SEP-165 defect, and the Relationships tab fills that key first. A call
    // that resolved to no Contact simply has no candidates, and the drawer then
    // behaves exactly as it does today.
    const contactId = item.contactId;
    const { data: relationships } = useRelationships(contactId ?? undefined);
    const assignableRecords = useMemo(
        () => assignableRecordsFrom(relationships?.rows ?? []),
        [relationships],
    );

    // The role catalogue for a person-to-person link (SEP-215). Only the
    // `contact_contact` rows apply, and the write boundary refuses any other
    // key, so the two selects offer nothing that could be rejected.
    const { data: rolesData } = useRelationshipRoles();
    const pairRoles = useMemo(
        () => (rolesData?.roles ?? []).filter((r) => r.active && r.appliesTo === 'contact_contact'),
        [rolesData],
    );
    const proposals = item.proposals;

    // Staged decisions keyed by artefact id (ids are unique across the three
    // call tables). Undecided rows are absent from the map.
    const [staged, setStaged] = useState<Record<string, Staged>>({});
    const [busy, setBusy] = useState(false);

    const stage = (id: string, kind: CallDraftKind, decision: StageDecision | undefined): void => {
        setStaged((prev) => {
            if (decision === undefined) {
                if (!(id in prev)) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            }
            // ADR-0015: a plain accept assigns nothing; a person with exactly
            // one record has that one record pre-ticked. Two or more is the
            // ambiguous case the reviewer must settle. A reviewer who already
            // answered the picker keeps their answer through a thumb-down and
            // back.
            const assignTo = assignmentForDecision(prev[id], decision, assignableRecords);
            return { ...prev, [id]: { ...prev[id], kind, decision, assignTo } };
        });
    };
    const stageTarget = (id: string, record: AssignableRecord): void => {
        setStaged((prev) => {
            const current = prev[id];
            if (!current) return prev;
            return {
                ...prev,
                [id]: {
                    ...current,
                    assignTo: toggleTarget(current.assignTo ?? [], record),
                    assignTouched: true,
                },
            };
        });
    };
    /** Copy one item's ticked records onto every other accepted item. The
     *  reporter's man answers for eleven records; without this the reviewer
     *  ticks the same box once per insight. */
    const applyTargetsToAllAccepted = (id: string): void => {
        setStaged((prev) => {
            const source = prev[id]?.assignTo ?? [];
            const next: Record<string, Staged> = {};
            for (const [key, value] of Object.entries(prev)) {
                next[key] =
                    value.decision === 'approve'
                        ? { ...value, assignTo: [...source], assignTouched: true }
                        : value;
            }
            return next;
        });
    };
    const stageReason = (id: string, reason: string): void => {
        setStaged((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], reason } } : prev));
    };
    /** A proposal stages like the rest, and it seeds its own promotion. */
    const stageProposal = (
        proposal: RelationshipProposalRow,
        decision: StageDecision | undefined,
    ): void => {
        setStaged((prev) => {
            if (decision === undefined) {
                if (!(proposal.id in prev)) return prev;
                const next = { ...prev };
                delete next[proposal.id];
                return next;
            }
            const current = prev[proposal.id];
            return {
                ...prev,
                [proposal.id]: {
                    ...current,
                    kind: 'relationship-proposal',
                    decision,
                    promoteTo: current?.promoteTo ?? initialPromotion(proposal),
                },
            };
        });
    };
    const stagePromotion = (id: string, promoteTo: RelationshipPromotion | undefined): void => {
        setStaged((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], promoteTo } } : prev));
    };

    const include = (status: AgentRowReviewStatus | null | undefined): boolean => {
        if (showReviewed) return true;
        return status == null || status === 'pending';
    };

    const facts = useMemo(
        () => allFacts.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allFacts, item.callId, showReviewed],
    );
    const actions = useMemo(
        () => allActions.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allActions, item.callId, showReviewed],
    );
    const notes = useMemo(
        () => allNotes.filter((r) => r.callId === item.callId && include(r.reviewStatus)),
        [allNotes, item.callId, showReviewed],
    );

    const pendingRows: Array<{ id: string; kind: CallDraftKind }> = [
        ...facts
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'call-extracted-fact' as const })),
        ...actions
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'call-action-item' as const })),
        ...notes
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'call-kb-note' as const })),
        ...proposals
            .filter((r) => r.reviewStatus == null || r.reviewStatus === 'pending')
            .map((r) => ({ id: r.id, kind: 'relationship-proposal' as const })),
    ];
    const pendingTotal = pendingRows.length;
    const stagedEntries = Object.entries(staged);
    const stagedCount = stagedEntries.length;
    const undecided = pendingTotal - stagedCount;

    // Stage every still-undecided pending row as accept (the bulk-happy path).
    // The reviewer can then thumb-down the few bad ones before confirming.
    function acceptAll(): void {
        setStaged((prev) => {
            const next = { ...prev };
            for (const r of pendingRows) {
                if (!(r.id in next)) {
                    const proposal =
                        r.kind === 'relationship-proposal'
                            ? proposals.find((p) => p.id === r.id)
                            : undefined;
                    next[r.id] = {
                        kind: r.kind,
                        decision: 'approve',
                        assignTo: preTickedTargets(assignableRecords),
                        promoteTo: proposal ? initialPromotion(proposal) : undefined,
                    };
                }
            }
            return next;
        });
    }

    async function confirmDecisions(): Promise<void> {
        // Accept and assign are ONE submission (ADR-0015), and so are approve
        // and promote (SEP-215). `buildDecision` holds the rule that the
        // staged answers ride an approve only, so this site no longer repeats
        // it once for each answer.
        const decisions: ReviewDecision[] = stagedEntries.map(([id, s]) =>
            s.kind === 'relationship-proposal'
                ? buildDecision({
                      kind: s.kind,
                      id,
                      decision: s.decision,
                      reason: s.reason,
                      payload: { promoteTo: s.promoteTo },
                  })
                : buildDecision({
                      kind: s.kind,
                      id,
                      decision: s.decision,
                      reason: s.reason,
                      payload: { assignTo: s.assignTo ?? [] },
                  }),
        );
        if (decisions.length === 0) return;
        setBusy(true);
        const batch = await submitReviewDecisions(decisions);
        setBusy(false);
        if (!batch.ok) {
            pushToast({ kind: 'error', message: batch.error ?? 'Failed to confirm decisions' });
            return;
        }
        const { confirmed, failed } = batch.summary;
        pushToast(
            failed > 0
                ? { kind: 'info', message: `Confirmed ${confirmed}, ${failed} failed` }
                : {
                      kind: 'success',
                      message: `Confirmed ${confirmed} decision${confirmed === 1 ? '' : 's'}`,
                  },
        );
        void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
        void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
        setStaged({});
        onCommitted?.();
    }

    return (
        <div className="flex flex-col gap-4">
            {headerBlocks.length > 0 && <ContextBlocks blocks={headerBlocks} />}

            {pendingTotal > 0 && stagedCount === 0 && (
                <div className="flex items-center justify-end">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={acceptAll}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Accept all ({pendingTotal})
                    </Button>
                </div>
            )}

            {facts.length === 0 &&
                actions.length === 0 &&
                notes.length === 0 &&
                proposals.length === 0 && (
                    <EmptyState title="Nothing left to review for this call." />
                )}

            {facts.length > 0 && (
                <SectionShell title="Extracted facts" count={facts.length}>
                    {facts.map((f) => (
                        <FactItem
                            key={f.id}
                            row={f}
                            staged={staged[f.id]?.decision}
                            reason={staged[f.id]?.reason ?? ''}
                            transcript={transcript}
                            onStage={(d) => stage(f.id, 'call-extracted-fact', d)}
                            onStageReason={(r) => stageReason(f.id, r)}
                            assignment={
                                <AssignmentPicker
                                    records={assignableRecords}
                                    selected={staged[f.id]?.assignTo ?? []}
                                    onToggle={(record) => stageTarget(f.id, record)}
                                    onApplyToAll={() => applyTargetsToAllAccepted(f.id)}
                                    canApplyToAll={stagedCount > 1}
                                />
                            }
                        />
                    ))}
                </SectionShell>
            )}

            {actions.length > 0 && (
                <SectionShell title="Action items" count={actions.length}>
                    {actions.map((a) => (
                        <ActionItem
                            key={a.id}
                            row={a}
                            staged={staged[a.id]?.decision}
                            reason={staged[a.id]?.reason ?? ''}
                            transcript={transcript}
                            onStage={(d) => stage(a.id, 'call-action-item', d)}
                            onStageReason={(r) => stageReason(a.id, r)}
                            assignment={
                                <AssignmentPicker
                                    records={assignableRecords}
                                    selected={staged[a.id]?.assignTo ?? []}
                                    onToggle={(record) => stageTarget(a.id, record)}
                                    onApplyToAll={() => applyTargetsToAllAccepted(a.id)}
                                    canApplyToAll={stagedCount > 1}
                                />
                            }
                        />
                    ))}
                </SectionShell>
            )}

            {notes.length > 0 && (
                <SectionShell title="KB notes" count={notes.length}>
                    {notes.map((n) => (
                        <NoteItem
                            key={n.id}
                            row={n}
                            staged={staged[n.id]?.decision}
                            reason={staged[n.id]?.reason ?? ''}
                            onStage={(d) => stage(n.id, 'call-kb-note', d)}
                            onStageReason={(r) => stageReason(n.id, r)}
                            assignment={
                                <AssignmentPicker
                                    records={assignableRecords}
                                    selected={staged[n.id]?.assignTo ?? []}
                                    onToggle={(record) => stageTarget(n.id, record)}
                                    onApplyToAll={() => applyTargetsToAllAccepted(n.id)}
                                    canApplyToAll={stagedCount > 1}
                                />
                            }
                        />
                    ))}
                </SectionShell>
            )}

            {proposals.length > 0 && (
                <SectionShell title="Proposed relationships" count={proposals.length}>
                    {proposals.map((p) => (
                        <ProposalItem
                            key={p.id}
                            row={p}
                            roles={pairRoles}
                            staged={staged[p.id]?.decision}
                            reason={staged[p.id]?.reason ?? ''}
                            promoteTo={staged[p.id]?.promoteTo}
                            onStage={(d) => stageProposal(p, d)}
                            onStageReason={(r) => stageReason(p.id, r)}
                            onPromoteChange={(promotion) => stagePromotion(p.id, promotion)}
                        />
                    ))}
                </SectionShell>
            )}

            {stagedCount > 0 && (
                // Full-bleed sticky footer, pinned flush to the scrollport
                // bottom. `-mx-card-padding` cancels the container's horizontal
                // padding; the container drops its *bottom* padding for this body
                // (ReviewItemPanel) so a plain `bottom-0` sits flush with no gap
                // and no scroll wobble (a negative offset would drift on scroll).
                <div className="sticky bottom-0 -mx-card-padding px-card-padding py-3 flex items-center justify-between gap-3 bg-[rgb(var(--color-page))] border-t border-[rgb(var(--color-divider))]">
                    <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                        {stagedCount} staged
                        {undecided > 0 ? ` · ${undecided} left pending` : ''}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setStaged({})}
                            disabled={busy}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => void confirmDecisions()}
                            disabled={busy}
                            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                        >
                            Confirm {stagedCount} decision{stagedCount === 1 ? '' : 's'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionShell({
    title,
    count,
    children,
}: {
    title: string;
    count: number;
    children: React.ReactNode;
}) {
    return (
        <section className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <h3 className="text-card-title text-[rgb(var(--color-text-primary))]">{title}</h3>
                <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                    {String(count).padStart(2, '0')}
                </span>
            </div>
            <ul className="flex flex-col gap-2">{children}</ul>
        </section>
    );
}

function StatusBadge({ status }: { status: AgentRowReviewStatus | null | undefined }) {
    if (status === 'approved') {
        return <Pill variant="green" size="sm" label="approved" />;
    }
    if (status === 'rejected') {
        return <Pill variant="red" size="sm" label="rejected" />;
    }
    if (status === 'revision_requested') {
        return <Pill variant="amber" size="sm" label="revision requested" />;
    }
    return <Pill variant="amber" size="sm" label="pending" />;
}

interface StagingRowProps {
    status: AgentRowReviewStatus | null | undefined;
    staged: StageDecision | undefined;
    onStage: (decision: StageDecision | undefined) => void;
}

// Tri-state thumbs: highlighted when staged; toggling the active thumb clears
// the staged decision (back to undecided). Reviewed rows render nothing here
// (their StatusBadge carries the disposition).
function StagingControls({ status, staged, onStage }: StagingRowProps) {
    if (status != null && status !== 'pending') return null;
    return (
        <div className="flex items-center gap-1">
            <Button
                variant={staged === 'approve' ? 'primary' : 'ghost'}
                size="sm"
                aria-pressed={staged === 'approve'}
                aria-label="Accept"
                title="Accept"
                onClick={() => onStage(staged === 'approve' ? undefined : 'approve')}
                icon={<ThumbsUp className="w-3.5 h-3.5" />}
            />
            <Button
                variant={staged === 'reject' ? 'danger' : 'ghost'}
                size="sm"
                aria-pressed={staged === 'reject'}
                aria-label="Reject"
                title="Reject"
                onClick={() => onStage(staged === 'reject' ? undefined : 'reject')}
                icon={<ThumbsDown className="w-3.5 h-3.5" />}
            />
        </div>
    );
}

// Optional inline reason, shown only when a row is staged for rejection (the
// server defaults an empty reason, so it stays optional).
function RejectReasonField({
    reason,
    onChange,
}: {
    reason: string;
    onChange: (r: string) => void;
}) {
    return (
        <input
            type="text"
            value={reason}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Why reject? (optional)"
            className={cn(
                'w-full rounded-button border border-[rgb(var(--color-divider))] bg-transparent',
                'px-2 py-1 text-annotation text-[rgb(var(--color-text-primary))]',
                'placeholder:text-[rgb(var(--color-text-tertiary))]',
                'focus:outline-none focus:border-[rgb(var(--color-accent))]',
            )}
        />
    );
}

/**
 * The record picker (SEP-183 / ADR-0015).
 *
 * The thumb answers "is this true?"; this answers "which record or records
 * does it apply to?". Both travel in ONE staged submission. Nothing ticked
 * means the insight stays with the person and lands on no record, which is the
 * ADR default.
 *
 * The tier word comes from the shared contract map, so this component cannot
 * disagree with the Relationships panel about what a tier is called. The old
 * label trap is gone with it: the Account tier's label now reads "Account
 * group", and "Client" names the registered holder alone.
 */
function AssignmentPicker({
    records,
    selected,
    onToggle,
    onApplyToAll,
    canApplyToAll,
}: {
    records: ReadonlyArray<AssignableRecord>;
    selected: ReadonlyArray<KnowledgeAssignmentTarget>;
    onToggle: (record: AssignableRecord) => void;
    onApplyToAll: () => void;
    canApplyToAll: boolean;
}) {
    if (records.length === 0) return null;
    return (
        <div className="flex flex-col gap-1 border-t border-[rgb(var(--color-divider))] pt-2">
            <div className="flex items-center justify-between gap-2">
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    Applies to
                </span>
                {canApplyToAll && (
                    <button
                        type="button"
                        onClick={onApplyToAll}
                        className="text-annotation text-[rgb(var(--color-accent))] hover:underline"
                    >
                        Same for all accepted
                    </button>
                )}
            </div>
            <div className="flex flex-col gap-1">
                {records.map((record) => (
                    <label
                        key={`${record.targetType}:${record.targetId}`}
                        className="flex items-center gap-2 text-annotation text-[rgb(var(--color-text-primary))]"
                    >
                        <input
                            type="checkbox"
                            checked={isTargetSelected(selected, record)}
                            onChange={() => onToggle(record)}
                            className="accent-[rgb(var(--color-accent))]"
                        />
                        <span>{record.label}</span>
                        <span className="text-[rgb(var(--color-text-tertiary))]">
                            {RELATIONSHIP_ENDPOINT_TYPE_LABELS[record.targetType]}
                        </span>
                    </label>
                ))}
            </div>
        </div>
    );
}

// Border tint reflecting the staged decision so the row reads at a glance.
function stagedBorder(staged: StageDecision | undefined): string {
    if (staged === 'approve') return 'border-[rgb(var(--color-accent))]';
    if (staged === 'reject') return 'border-[rgb(var(--color-rag-red))]';
    return 'border-[rgb(var(--color-divider))]';
}

interface StagedItemProps<R> {
    row: R;
    staged: StageDecision | undefined;
    reason: string;
    /** Call transcript lines (call drafts) for the cited-line expander. */
    transcript?: ContextTranscriptLine[] | null;
    onStage: (decision: StageDecision | undefined) => void;
    onStageReason: (reason: string) => void;
    /** The record picker (SEP-183), rendered only once the thumb is up. */
    assignment?: ReactNode;
}

function FactItem({
    row,
    staged,
    reason,
    transcript,
    onStage,
    onStageReason,
    assignment,
}: StagedItemProps<CallFactRow>) {
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1.5',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <Pill variant="grey" size="sm" label={row.category} />
                {/* The column is nullable. A row with no band showed an
                    "undefined confidence" pill in red, which read as low
                    confidence. Say nothing instead. */}
                {row.confidence && (
                    <Pill
                        variant={confidenceVariant(row.confidence)}
                        size="sm"
                        label={`${row.confidence} confidence`}
                    />
                )}
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.text}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            {row.sourceQuote && (
                <div className="flex items-start gap-1.5 text-annotation italic text-[rgb(var(--color-text-secondary))]">
                    <Quote className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.sourceQuote}</span>
                </div>
            )}
            {row.complianceRelevant && (
                <div className="flex items-start gap-1.5 text-annotation text-[rgb(var(--color-rag-amber))]">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.complianceReason ?? 'Compliance-relevant'}</span>
                </div>
            )}
            <CitedTranscriptExpander lines={transcript ?? null} lineId={row.sourceLineId} />
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
            {staged === 'approve' && assignment}
        </li>
    );
}

function confidenceVariant(c: 'high' | 'medium' | 'low'): 'green' | 'amber' | 'red' {
    return c === 'high' ? 'green' : c === 'medium' ? 'amber' : 'red';
}

function ActionItem({
    row,
    staged,
    reason,
    transcript,
    onStage,
    onStageReason,
    assignment,
}: StagedItemProps<CallActionItemRow>) {
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.title}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                {row.owner} · {row.dueDescription}
                {row.note ? ` · ${row.note}` : ''}
            </div>
            <CitedTranscriptExpander lines={transcript ?? null} lineId={row.sourceLineId} />
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
            {staged === 'approve' && assignment}
        </li>
    );
}

function NoteItem({
    row,
    staged,
    reason,
    onStage,
    onStageReason,
    assignment,
}: StagedItemProps<CallKbNoteRow>) {
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1.5',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <span className="flex-1 text-body-medium font-medium text-[rgb(var(--color-text-primary))]">
                    {row.title}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            <CitedSummary body={row.body} callId={row.callId} className="text-annotation" />
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
            {staged === 'approve' && assignment}
        </li>
    );
}

/**
 * A proposed person-to-person relationship (SEP-215).
 *
 * It reads like the three siblings above — the claim, the confidence, the
 * verbatim quote, a thumb — and it carries one thing they do not: the
 * counterparty election. ATLAS gives a NAME, and a name binds nothing, so the
 * reviewer says which Contact the name IS before a `Relationship` is written.
 *
 * There is no approve control on the knowledge graph. The graph draws a
 * proposal dashed and links here, and here is the only door.
 */
function ProposalItem({
    row,
    roles,
    staged,
    reason,
    promoteTo,
    onStage,
    onStageReason,
    onPromoteChange,
}: {
    row: RelationshipProposalRow;
    roles: ReadonlyArray<RelationshipRoleDto>;
    staged: StageDecision | undefined;
    reason: string;
    promoteTo: RelationshipPromotion | undefined;
    onStage: (decision: StageDecision | undefined) => void;
    onStageReason: (reason: string) => void;
    onPromoteChange: (promotion: RelationshipPromotion | undefined) => void;
}) {
    const roleLabel =
        roles.find((r) => r.key === row.proposedRole)?.label ??
        row.proposedRoleLabel ??
        row.proposedRole;
    return (
        <li
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1.5',
                stagedBorder(staged),
            )}
        >
            <div className="flex items-start gap-2 flex-wrap">
                <Pill variant="grey" size="sm" label={roleLabel} />
                {row.confidence && (
                    <Pill
                        variant={confidenceVariant(row.confidence)}
                        size="sm"
                        label={`${row.confidence} confidence`}
                    />
                )}
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {row.contactName
                        ? `${row.personName} — ${roleLabel.toLowerCase()} of ${row.contactName}`
                        : row.personName}
                </span>
                <StatusBadge status={row.reviewStatus} />
                <StagingControls status={row.reviewStatus} staged={staged} onStage={onStage} />
            </div>
            {row.sourceQuote && (
                <div className="flex items-start gap-1.5 text-annotation italic text-[rgb(var(--color-text-secondary))]">
                    <Quote className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{row.sourceQuote}</span>
                </div>
            )}
            {staged === 'reject' && <RejectReasonField reason={reason} onChange={onStageReason} />}
            {staged === 'approve' && (
                <CounterpartyElection
                    row={row}
                    roles={roles}
                    value={promoteTo}
                    onChange={onPromoteChange}
                />
            )}
        </li>
    );
}

/** Which of the three answers the reviewer is giving about the other person. */
type ElectionMode = 'existing-contact' | 'new-draft-contact' | 'later';

function electionModeOf(value: RelationshipPromotion | undefined): ElectionMode {
    if (!value) return 'later';
    return value.counterparty.kind;
}

/**
 * "Who is this person, and how are the two related?".
 *
 * Three answers, and the server never guesses between them. EXISTING opens on
 * the server's top-ranked candidate together with the reason it was offered,
 * because the ranking suggests and never binds: a telephone number is evidence,
 * a matching surname is not. The picker under it is the SAME book-scoped
 * counterpart picker the Relationships panel uses, so the rows on offer are
 * exactly the rows this reader may already open and scope stays server-side
 * (ADR-0005). NEW creates a draft Contact in the reviewer's own book from the
 * two name boxes. LATER approves the claim and writes no link, which is the
 * honest answer while nobody can say who the person is.
 */
function CounterpartyElection({
    row,
    roles,
    value,
    onChange,
}: {
    row: RelationshipProposalRow;
    roles: ReadonlyArray<RelationshipRoleDto>;
    value: RelationshipPromotion | undefined;
    onChange: (promotion: RelationshipPromotion | undefined) => void;
}) {
    const mode = electionModeOf(value);
    const subjectRole = value?.subjectRole ?? row.proposedRole;
    const counterpartyRole = value?.counterpartyRole ?? row.proposedRole;
    const suggestion = row.counterpartySuggestions[0] ?? null;
    const picked =
        value?.counterparty.kind === 'existing-contact' ? value.counterparty.contactId : '';
    const draftName = splitPersonName(row.personName);
    const firstName =
        value?.counterparty.kind === 'new-draft-contact'
            ? value.counterparty.firstName
            : draftName.firstName;
    const lastName =
        value?.counterparty.kind === 'new-draft-contact'
            ? value.counterparty.lastName
            : draftName.lastName;

    const setMode = (next: ElectionMode): void => {
        if (next === 'later') {
            onChange(undefined);
            return;
        }
        if (next === 'existing-contact') {
            onChange({
                counterparty: { kind: 'existing-contact', contactId: suggestion?.contactId ?? '' },
                subjectRole,
                counterpartyRole,
            });
            return;
        }
        onChange({
            counterparty: { kind: 'new-draft-contact', firstName, lastName },
            subjectRole,
            counterpartyRole,
        });
    };

    return (
        <div className="flex flex-col gap-2 border-t border-[rgb(var(--color-divider))] pt-2">
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Who is {row.personName}?
            </span>
            <div className="flex items-center gap-3 flex-wrap">
                {(
                    [
                        ['existing-contact', 'A contact I already hold'],
                        ['new-draft-contact', 'Create a new contact'],
                        ['later', 'Decide later — record no link'],
                    ] as ReadonlyArray<[ElectionMode, string]>
                ).map(([key, label]) => (
                    <label
                        key={key}
                        className="flex items-center gap-1.5 text-annotation text-[rgb(var(--color-text-primary))]"
                    >
                        <input
                            type="radio"
                            name={`counterparty-${row.id}`}
                            checked={mode === key}
                            onChange={() => setMode(key)}
                            className="accent-[rgb(var(--color-accent))]"
                        />
                        <span>{label}</span>
                    </label>
                ))}
            </div>

            {mode === 'existing-contact' && (
                <div className="flex flex-col gap-1.5">
                    {suggestion && (
                        <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                            Suggested: {suggestion.name}. {suggestion.reason}
                        </p>
                    )}
                    <CounterpartPicker
                        type="contact"
                        value={picked}
                        onPick={(contactId) =>
                            onChange({
                                counterparty: { kind: 'existing-contact', contactId },
                                subjectRole,
                                counterpartyRole,
                            })
                        }
                    />
                </div>
            )}

            {mode === 'new-draft-contact' && (
                <div className="flex items-center gap-2 flex-wrap">
                    <ElectionText
                        label="First name"
                        value={firstName}
                        onChange={(next) =>
                            onChange({
                                counterparty: {
                                    kind: 'new-draft-contact',
                                    firstName: next,
                                    lastName,
                                },
                                subjectRole,
                                counterpartyRole,
                            })
                        }
                    />
                    <ElectionText
                        label="Last name"
                        value={lastName}
                        onChange={(next) =>
                            onChange({
                                counterparty: {
                                    kind: 'new-draft-contact',
                                    firstName,
                                    lastName: next,
                                },
                                subjectRole,
                                counterpartyRole,
                            })
                        }
                    />
                </div>
            )}

            {value && (
                <div className="flex items-center gap-2 flex-wrap">
                    <ElectionRole
                        label="Their role"
                        value={counterpartyRole}
                        roles={roles}
                        onChange={(next) => onChange({ ...value, counterpartyRole: next })}
                    />
                    <ElectionRole
                        label={`${row.contactName ?? 'This person'}’s role`}
                        value={subjectRole}
                        roles={roles}
                        onChange={(next) => onChange({ ...value, subjectRole: next })}
                    />
                </div>
            )}
        </div>
    );
}

function ElectionText({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <input
            type="text"
            value={value}
            aria-label={label}
            placeholder={label}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
                'h-8 px-2 rounded-button border border-[rgb(var(--color-divider))] bg-transparent',
                'text-annotation text-[rgb(var(--color-text-primary))]',
                'placeholder:text-[rgb(var(--color-text-tertiary))]',
                'focus:outline-none focus:border-[rgb(var(--color-accent))]',
            )}
        />
    );
}

function ElectionRole({
    label,
    value,
    roles,
    onChange,
}: {
    label: string;
    value: string;
    roles: ReadonlyArray<RelationshipRoleDto>;
    onChange: (value: string) => void;
}) {
    return (
        <label className="flex items-center gap-1.5">
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">{label}</span>
            <Select size="sm" value={value} onChange={(e) => onChange(e.target.value)}>
                <option value="">Select role…</option>
                {roles.map((role) => (
                    <option key={role.key} value={role.key}>
                        {role.label}
                    </option>
                ))}
            </Select>
        </label>
    );
}

// -------- Monthly narrative body --------

export function MonthlyNarrativeBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: MonthlyNarrativeReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    const allDrafts = useMonthlyNarrativeDrafts();
    const { pushToast } = useToast();
    const queryClient = useQueryClient();
    const draft = useMemo(
        () => allDrafts.find((d) => d.id === item.draftId),
        [allDrafts, item.draftId],
    );
    const [activeTone, setActiveTone] = useState<string | null>(null);
    const [rejecting, setRejecting] = useState(false);
    const [requestingRevision, setRequestingRevision] = useState(false);

    if (!draft) {
        return (
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Draft data unavailable — it may have just been removed.
            </div>
        );
    }

    if (!showReviewed && draft.reviewStatus && draft.reviewStatus !== 'pending') {
        return (
            <div className="text-annotation text-[rgb(var(--color-text-tertiary))] italic">
                Reviewed. Toggle "Show reviewed" above to see decided drafts.
            </div>
        );
    }

    const currentTone =
        activeTone ??
        draft.variants[draft.selectedVariantIndex]?.tone ??
        draft.variants[0]?.tone ??
        'plain';
    const variant = draft.variants.find((v) => v.tone === currentTone) ?? draft.variants[0];

    async function handleApprove(): Promise<void> {
        if (!variant) return;
        const idx = draft!.variants.findIndex((v) => v.tone === currentTone);
        const result = await approveReviewable('monthly-narrative-draft', draft!.id, {
            selection: {
                selectedVariantIndex: idx >= 0 ? idx : 0,
                performanceSummary: variant.performanceSummary,
                narrativeParagraph: variant.narrativeParagraph,
            },
        });
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Narrative approved' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        }
    }

    async function handleReject(reason: string): Promise<void> {
        const result = await rejectReviewable(
            'monthly-narrative-draft',
            draft!.id,
            reason || undefined,
        );
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Narrative rejected' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        }
    }

    async function handleRequestRevision(reason: string): Promise<void> {
        const result = await requestRevisionReviewable(
            'monthly-narrative-draft',
            draft!.id,
            reason || 'No reason given',
        );
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Revision requested — ATLAS will re-roll' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        }
    }

    return (
        <div className="flex flex-col gap-3">
            <ContextSection subjectKind="monthly-narrative-draft" id={item.draftId} />
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={draft.reviewStatus} />
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {draft.kbNotesCited.length} KB note
                    {draft.kbNotesCited.length === 1 ? '' : 's'} cited
                </span>
            </div>
            <div className="flex gap-1 border-b border-[rgb(var(--color-divider))]">
                {draft.variants.map((v) => (
                    <button
                        key={v.tone}
                        type="button"
                        onClick={() => setActiveTone(v.tone)}
                        className={cn(
                            'px-3 py-1.5 text-annotation font-medium border-b-2 -mb-px',
                            currentTone === v.tone
                                ? 'border-[rgb(var(--color-accent))] text-[rgb(var(--color-accent))]'
                                : 'border-transparent text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))]',
                        )}
                    >
                        {v.tone}
                    </button>
                ))}
            </div>
            {variant && (
                <div className="flex flex-col gap-2">
                    <div className="text-body-medium font-medium text-[rgb(var(--color-text-primary))]">
                        {variant.performanceSummary}
                    </div>
                    <div className="text-body-medium text-[rgb(var(--color-text-secondary))] whitespace-pre-wrap">
                        {variant.narrativeParagraph}
                    </div>
                </div>
            )}
            {(draft.reviewStatus == null || draft.reviewStatus === 'pending') && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRejecting(true)}
                        icon={<ThumbsDown className="w-3.5 h-3.5" />}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setRequestingRevision(true)}
                        icon={<RotateCcw className="w-3.5 h-3.5" />}
                    >
                        Request revision
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleApprove()}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Approve {currentTone}
                    </Button>
                </div>
            )}
            <RejectionDialog
                open={rejecting}
                title="Reject narrative?"
                subject={`${item.primaryLabel} · ${item.period}`}
                placeholder="Optional — note why so ATLAS can re-roll."
                onConfirm={(reason) => void handleReject(reason)}
                onClose={() => setRejecting(false)}
            />
            <RejectionDialog
                open={requestingRevision}
                tone="neutral"
                title="Request revision?"
                subject={`${item.primaryLabel} · ${item.period}`}
                placeholder="Tell ATLAS what to change so it can re-roll the narrative."
                confirmLabel="Request revision"
                onConfirm={(reason) => void handleRequestRevision(reason)}
                onClose={() => setRequestingRevision(false)}
            />
        </div>
    );
}

// -------- CEDAR coac-action-draft body (ADR-0020, editable correction) --------
//
// Unlike every other detector-style finding, CoacActionDraft's approval flow
// lets Ops correct the extracted fields before they materialise a live
// CoacAction. Plain controlled inputs, not a full form library —
// `coacActionDraftExtraPatch` on the server applies whichever of these fields
// changed before the reviewStatus flip + materialise.

function Field({
    label,
    value,
    onChange,
    disabled,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    disabled: boolean;
    placeholder?: string;
}) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">{label}</span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                className={cn(
                    'w-full rounded-button border border-[rgb(var(--color-divider))] bg-transparent',
                    'px-2.5 py-1.5 text-body-medium text-[rgb(var(--color-text-primary))]',
                    'placeholder:text-[rgb(var(--color-text-tertiary))]',
                    'focus:outline-none focus:border-[rgb(var(--color-accent))]',
                    'disabled:opacity-60',
                )}
            />
        </label>
    );
}

// -------- Notice dates on a CoacActionDraft (SEP-201) --------
//
// CEDAR keeps the date text of the notice verbatim, because it converts
// nothing. The SERVER reads that text and sends the reading beside it, as a
// `CoacNoticeDate`. This file never parses a date. A reading that failed stops
// the approval here, and the approval Action refuses it again on the server.

/** Says in plain words why the server could not read the date text. */
const NOTICE_DATE_PROBLEM_TEXT: Record<NonNullable<CoacNoticeDate['problem']>, string> = {
    not_stated_order: 'This date is not in US order',
    unrecognised: 'This date cannot be read',
    out_of_range: 'This year is out of range',
};

/** The reading of the server for one notice date, under its input box. */
function NoticeDateAnnotation({ notice, value }: { notice: CoacNoticeDate; value: string }) {
    if (!notice.raw || !readingIsCurrent(notice, value)) return null;
    if (notice.iso) {
        return (
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Reads as {formatDate(notice.iso)}
            </span>
        );
    }
    if (!notice.problem) return null;
    return (
        <span className="flex items-start gap-1.5 text-annotation text-[rgb(var(--color-rag-amber))]">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{NOTICE_DATE_PROBLEM_TEXT[notice.problem]}</span>
        </span>
    );
}

/** One editable notice date: the box, then the reading of the server. */
function NoticeDateField({
    label,
    notice,
    value,
    onChange,
    disabled,
}: {
    label: string;
    notice: CoacNoticeDate;
    value: string;
    onChange: (v: string) => void;
    disabled: boolean;
}) {
    return (
        <div className="flex flex-col gap-1">
            <Field
                label={label}
                value={value}
                onChange={onChange}
                disabled={disabled}
                placeholder="none"
            />
            <NoticeDateAnnotation notice={notice} value={value} />
        </div>
    );
}

// -------- The election options of the notice (SEP-251 request 8) --------
//
// The notice prints the choices that a holder has, and those choices are the
// decision list the IM later sees on the Desk. The extraction reads them, and
// Ops confirms them HERE, before the approval materialises the live corporate
// action. A wrong list is a wrong instruction to the custodian, so the reviewer
// gets the whole list, not a summary of it.
//
// The reviewer can change the text of an option, change which option is the
// default, remove an option that the extraction invented, and add one that it
// missed. The default is a radio group, because a notice states one default or
// none. The server refuses an unusable list before any write.

function ElectionOptionsField({
    options,
    onChange,
    disabled,
}: {
    options: CoacElectionOption[] | null;
    onChange: (next: CoacElectionOption[] | null) => void;
    disabled: boolean;
}) {
    const defaultName = useId();

    function patch(index: number, part: Partial<CoacElectionOption>): void {
        onChange((options ?? []).map((o, i) => (i === index ? { ...o, ...part } : o)));
    }

    function markDefault(index: number | null): void {
        onChange((options ?? []).map((o, i) => ({ ...o, isDefault: i === index })));
    }

    return (
        <div className="flex flex-col gap-2">
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Election options of the notice
            </span>

            {options === null && (
                <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                    Nobody read a notice for this draft, so it carries no option list. The Desk sees
                    no printed choice.
                </p>
            )}
            {options !== null && options.length === 0 && (
                <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                    This notice prints no election option.
                </p>
            )}

            {(options ?? []).map((o, i) => (
                <div key={`${i}-${o.code}`} className="flex items-center gap-2">
                    <input
                        type="text"
                        value={o.code}
                        disabled={disabled}
                        aria-label={`Code of option ${i + 1}`}
                        onChange={(e) => patch(i, { code: e.target.value })}
                        className="h-7 w-[72px] rounded-search border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))] px-2 text-annotation font-mono tabular-nums text-[rgb(var(--color-text-primary))]"
                    />
                    <input
                        type="text"
                        value={o.label}
                        disabled={disabled}
                        aria-label={`Text of option ${i + 1}`}
                        onChange={(e) => patch(i, { label: e.target.value })}
                        className="h-7 flex-1 min-w-0 rounded-search border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))] px-2 text-annotation text-[rgb(var(--color-text-primary))]"
                    />
                    <label className="flex items-center gap-1 text-annotation text-[rgb(var(--color-text-secondary))] cursor-pointer">
                        <input
                            type="radio"
                            name={defaultName}
                            checked={o.isDefault}
                            disabled={disabled}
                            onChange={() => markDefault(i)}
                            className="accent-[rgb(var(--color-accent))]"
                        />
                        Default
                    </label>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange((options ?? []).filter((_, j) => j !== i))}
                        className="text-annotation text-[rgb(var(--color-text-tertiary))] hover:text-[rgb(var(--color-rag-red))] underline"
                    >
                        Remove
                    </button>
                </div>
            ))}

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() =>
                        onChange([...(options ?? []), { code: '', label: '', isDefault: false }])
                    }
                    className="text-annotation text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))] underline"
                >
                    Add option
                </button>
                {options !== null && options.some((o) => o.isDefault) && (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => markDefault(null)}
                        className="text-annotation text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))] underline"
                    >
                        The notice states no default
                    </button>
                )}
            </div>
        </div>
    );
}

export function CoacActionDraftBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: CoacActionDraftReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    const coacDrafts = useCoacActionDrafts();
    const draft = useMemo(
        () => coacDrafts.find((d) => d.id === item.draftId),
        [coacDrafts, item.draftId],
    );
    const { pushToast } = useToast();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(false);

    const [securityName, setSecurityName] = useState(item.securityName);
    const [eventType, setEventType] = useState(item.eventType);
    // The four notice dates, as the verbatim text of the document. The reviewer
    // edits the text, and the server reads it (SEP-201).
    const [responseDeadline, setResponseDeadline] = useState(item.responseDeadline.raw ?? '');
    const [payDate, setPayDate] = useState(item.payDate.raw ?? '');
    const [recordDate, setRecordDate] = useState(item.recordDate.raw ?? '');
    const [exDate, setExDate] = useState(item.exDate.raw ?? '');
    const [wealthManager, setWealthManager] = useState(item.wealthManager ?? '');
    // The options that the notice prints. The reviewer confirms or corrects the
    // WHOLE list, because a correction of one option is a correction of the set
    // that the custodian recognises (SEP-251).
    const [electionOptions, setElectionOptions] = useState<CoacElectionOption[] | null>(
        item.electionOptions,
    );
    const [correctionNote, setCorrectionNote] = useState('');

    // A date that the server could not read blocks the approval, because an
    // unreadable regulated date must never reach a live row. The rule itself is
    // `blockReason` in `@/lib/review-decision` — one shape for the greyed
    // button and the message beside it, and a node test for the rule that no
    // browser has to run. The server precheck is the other enforcement point.
    const approveBlockedBecause =
        blockReason([
            { notice: item.responseDeadline, value: responseDeadline },
            { notice: item.payDate, value: payDate },
            { notice: item.recordDate, value: recordDate },
            { notice: item.exDate, value: exDate },
        ]) ?? coacElectionOptionsProblem(electionOptions);

    const status = draft?.reviewStatus ?? 'pending';
    if (!showReviewed && status !== 'pending') {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                This draft has been reviewed.
            </p>
        );
    }

    async function handleApprove(): Promise<void> {
        setBusy(true);
        const result = await approveReviewable('coac-action-draft', item.draftId, {
            correction: {
                securityName,
                eventType,
                // The four dates travel as plain text, because the reviewer
                // types free text and the server does the reading (SEP-201).
                responseDeadline: responseDeadline || null,
                payDate: payDate || null,
                recordDate: recordDate || null,
                exDate: exDate || null,
                wealthManager: wealthManager || null,
                // The whole list travels, so `null` (nobody read a notice) and
                // `[]` (the notice prints no option) stay apart on the wire.
                electionOptions,
                ...(correctionNote ? { correctionNote } : {}),
            },
        });
        setBusy(false);
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Corporate action approved' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        } else {
            pushToast({ kind: 'error', message: result.error ?? 'Approve failed' });
        }
    }

    async function handleReject(reason: string): Promise<void> {
        setBusy(true);
        const result = await rejectReviewable(
            'coac-action-draft',
            item.draftId,
            reason || undefined,
        );
        setBusy(false);
        if (result.ok) {
            pushToast({ kind: 'success', message: 'Draft rejected' });
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
            onCommitted?.();
        } else {
            pushToast({ kind: 'error', message: result.error ?? 'Reject failed' });
        }
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={status} />
                <span className="text-annotation font-mono text-[rgb(var(--color-text-tertiary))]">
                    ref {draft?.sourceReferenceId ?? item.sourceReferenceId}
                </span>
                {item.securityId && (
                    <span className="text-annotation font-mono text-[rgb(var(--color-text-tertiary))]">
                        {item.securityId}
                    </span>
                )}
            </div>

            {(draft?.narrative ?? item.narrative) && (
                <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug whitespace-pre-wrap">
                    {draft?.narrative ?? item.narrative}
                </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                    label="Security name"
                    value={securityName}
                    onChange={setSecurityName}
                    disabled={busy || status !== 'pending'}
                />
                <Field
                    label="Event type"
                    value={eventType}
                    onChange={setEventType}
                    disabled={busy || status !== 'pending'}
                />
                <NoticeDateField
                    label="Response deadline"
                    notice={item.responseDeadline}
                    value={responseDeadline}
                    onChange={setResponseDeadline}
                    disabled={busy || status !== 'pending'}
                />
                <NoticeDateField
                    label="Payable date"
                    notice={item.payDate}
                    value={payDate}
                    onChange={setPayDate}
                    disabled={busy || status !== 'pending'}
                />
                <NoticeDateField
                    label="Record date"
                    notice={item.recordDate}
                    value={recordDate}
                    onChange={setRecordDate}
                    disabled={busy || status !== 'pending'}
                />
                <NoticeDateField
                    label="Ex date"
                    notice={item.exDate}
                    value={exDate}
                    onChange={setExDate}
                    disabled={busy || status !== 'pending'}
                />
                <Field
                    label="Wealth manager"
                    value={wealthManager}
                    onChange={setWealthManager}
                    disabled={busy || status !== 'pending'}
                    placeholder="none"
                />
            </div>

            <ElectionOptionsField
                options={electionOptions}
                onChange={setElectionOptions}
                disabled={busy || status !== 'pending'}
            />

            {status === 'pending' && (
                <Field
                    label="Correction note (optional)"
                    value={correctionNote}
                    onChange={setCorrectionNote}
                    disabled={busy}
                    placeholder="What did you change and why?"
                />
            )}

            {status === 'pending' && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                    {approveBlockedBecause && (
                        <span className="flex items-start gap-1.5 mr-auto text-annotation text-[rgb(var(--color-rag-amber))]">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{approveBlockedBecause}</span>
                        </span>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRejecting(true)}
                        disabled={busy}
                        icon={<ThumbsDown className="w-3.5 h-3.5" />}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleApprove()}
                        disabled={busy || approveBlockedBecause !== null}
                        icon={<ThumbsUp className="w-3.5 h-3.5" />}
                    >
                        Approve
                    </Button>
                </div>
            )}

            <RejectionDialog
                open={rejecting}
                title="Reject corporate action draft?"
                subject={item.primaryLabel}
                placeholder="Optional — note why so Ops knows not to re-upload as-is."
                onConfirm={(reason) => void handleReject(reason)}
                onClose={() => setRejecting(false)}
            />
        </div>
    );
}

// -------- Composer draft body (generic <CompositionWorkbench>) --------
//
// Renders the per-agent composition-workbench wrapper for a pending
// `*Draft` row. The draft row is re-fetched here by id from its table so
// the workbench gets the full envelope (brief / sources / plan / variants
// / handoff / citations). Approve / reject / request-revision mutations are
// owned by the generic <CompositionWorkbench>.

export function ComposerDraftBody({
    item,
    showReviewed,
    onCommitted,
}: {
    item: ComposerDraftReviewItem;
    showReviewed: boolean;
    onCommitted?: () => void;
}) {
    // `item.table` is `AgentRowTable` (contract allow-list) — fetching a
    // non-allow-listed (e.g. finding) table here is a compile error.
    const rows = useComposerDrafts(item.table);
    const draft = useMemo(
        () => rows.find((d) => d.id === item.draftId) ?? null,
        [rows, item.draftId],
    );

    if (!draft) {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Draft no longer available.
            </p>
        );
    }
    if (!showReviewed && draft.reviewStatus && draft.reviewStatus !== 'pending') {
        return (
            <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                This draft has been reviewed.
            </p>
        );
    }

    // Close the panel on any committed decision (approve / reject /
    // request-revision) — the workbench owns the mutation + toast.
    const hooks = {
        onApproved: onCommitted,
        onRejected: onCommitted,
        onRevisionRequested: onCommitted,
    };
    // One kind, one workbench. The seven per-agent wrapper files and the
    // switch over them are gone: the kind gives the eyebrow, the wiring and
    // the two optional blocks.
    const kind = workbenchKindForAgent(item.agent);
    return (
        <div className="flex flex-col gap-3">
            <ContextSection subjectKind={kind} id={item.draftId} />
            <CompositionWorkbench
                kind={kind}
                draft={draft}
                renderInvariants={invariantsFor(kind) ?? undefined}
                renderSections={sectionsFor(kind) ?? undefined}
                {...hooks}
            />
        </div>
    );
}

// -------- Task body (generic AgentTask review_required) --------

export function TaskBody({
    item,
    onCommitted,
}: {
    item: TaskReviewItem;
    onCommitted?: () => void;
}) {
    const { reviewTask } = useAgent();
    const { user } = useSession();
    const canOpenDraft = canOpenRoute(item.contextHref, user?.personas ?? []);
    const task = item.task;
    const summary = describeTaskOutput(task);
    const [rejecting, setRejecting] = useState(false);

    const decide = (action: 'approve' | 'reject', reason?: string): void => {
        void reviewTask(task.id, action, reason).then((ok) => {
            if (ok) onCommitted?.();
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status="pending" />
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {item.secondaryLabel}
                </span>
            </div>
            {task.artefactType && task.artefactId && (
                <ContextSection subjectKind={task.artefactType} id={task.artefactId} />
            )}
            {summary && (
                <p className="text-body-medium text-[rgb(var(--color-text-secondary))] leading-snug">
                    {summary}
                </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
                <DownloadRow task={task} />
                <ExportToSharePointButton task={task} />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgb(var(--color-divider)/0.5)]">
                {canOpenDraft && (
                    <Link
                        to={item.contextHref}
                        className={cn(
                            'inline-flex items-center gap-1.5 px-3 py-1.5 text-annotation rounded-button',
                            'border border-[rgb(var(--color-divider))]',
                            'text-[rgb(var(--color-text-primary))]',
                            'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                            'transition-colors duration-card-hover ease-swiss',
                        )}
                    >
                        <FileText className="w-3.5 h-3.5" />
                        Open full draft
                    </Link>
                )}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRejecting(true)}
                    icon={<XCircle className="w-3.5 h-3.5" />}
                >
                    Reject
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => decide('approve')}
                    icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                >
                    Approve
                </Button>
            </div>
            <RejectionDialog
                open={rejecting}
                subject={`${item.primaryLabel} · ${item.secondaryLabel}`}
                onConfirm={(reason) => decide('reject', reason || undefined)}
                onClose={() => setRejecting(false)}
            />
        </div>
    );
}

// One-line summary of a task's result, derived from its primary produced
// artefact row (PRD #261 follow-up — the legacy `output` union is no longer
// populated by compose). `summariseArtefact` reads the shared content fields
// (summary / headline / subject / claim / variant body) defensively.
export function describeTaskOutput(task: AgentTask): string | null {
    const row = task.artefacts?.[0]?.row;
    return row ? summariseArtefact(row) : null;
}
